import React, { useEffect, useState } from 'react'
import { Slider, TextField } from '@mui/material'

import { BudgetStepProps } from '../types'

const BudgetStep: React.FC<BudgetStepProps> = ({ value, onChange, error, min = 0, max = 5000, step = 50 }) => {
  const [budgetRange, setBudgetRange] = useState<number[]>(value)

  // Initialize budget range
  useEffect(() => {
    if (value && value.length === 2) {
      setBudgetRange(value)
    }
  }, [value])

  const updateValue = (newRange: number[]) => {
    onChange([newRange[0], newRange[1]])
  }

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    const range = newValue as number[]
    setBudgetRange(range)
    updateValue(range)
  }

  const handleMinInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newMin = Number(event.target.value)
    const maxBudget = budgetRange[1]

    // Validate range
    if (newMin < min) newMin = min
    if (newMin > max) newMin = max

    // Ensure min is not greater than max
    if (newMin > maxBudget) {
      newMin = maxBudget
    }

    const newRange = [newMin, maxBudget]
    setBudgetRange(newRange)
    updateValue(newRange)
  }

  const handleMaxInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newMax = Number(event.target.value)
    const minBudget = budgetRange[0]

    // Validate range
    if (newMax < min) newMax = min
    if (newMax > max) newMax = max

    // Ensure max is not less than min
    if (newMax < minBudget) {
      newMax = minBudget
    }

    const newRange = [minBudget, newMax]
    setBudgetRange(newRange)
    updateValue(newRange)
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-bold text-[#1d2129] leading-snug">4. 月租预算范围（美元/月）</h2>
      </div>

      <div className="flex flex-col gap-4 px-1">
        {/* 输入框 */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
            <TextField
              type="number"
              value={budgetRange[0]}
              onChange={handleMinInputChange}
              fullWidth
              size="small"
              InputProps={{
                sx: {
                  paddingLeft: '28px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: '#e0e2ef',
                  },
                  '& input': {
                    paddingLeft: '8px',
                  },
                },
                inputProps: {
                  min,
                  max,
                  step,
                },
              }}
            />
          </div>
          <span className="text-gray-400 font-medium">-</span>
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
            <TextField
              type="number"
              value={budgetRange[1]}
              onChange={handleMaxInputChange}
              fullWidth
              size="small"
              InputProps={{
                sx: {
                  paddingLeft: '28px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: '#e0e2ef',
                  },
                  '& input': {
                    paddingLeft: '8px',
                  },
                },
                inputProps: {
                  min,
                  max,
                  step,
                },
              }}
            />
          </div>
        </div>

        {/* 滑块 */}
        <div className="pt-2 pb-2 px-2">
          <Slider
            value={budgetRange}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            min={min}
            max={max}
            step={step}
            valueLabelFormat={value => `$${value}`}
            sx={{
              color: '#050a22',
              '& .MuiSlider-thumb': {
                backgroundColor: '#050a22',
              },
              '& .MuiSlider-track': {
                backgroundColor: '#050a22',
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#e0e2ef',
              },
            }}
          />
        </div>

        {/* 范围标签 */}
        <div className="flex justify-between text-xs text-gray-400 font-medium px-1">
          <span>${min}</span>
          <span>${max}+</span>
        </div>
      </div>

      {error && <p className="text-red-500 text-sm font-medium">此问题为必填项</p>}
    </div>
  )
}

export default BudgetStep
